(function () {
  const ready = (callback) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
      return;
    }
    callback();
  };

  ready(() => {
    const root = document.querySelector(".anou-soft-track-b");
    if (!root) return;

    setupImageShells(root);
    setupMiniQuiz(root);
    setupHeroSupport(root);
    setupFormulaDock(root);
    setupFormulaPage(root);
    setupMenus(root);
    setupMobileTopbar(root);
    setupFooterMotion(root);
    setupStandardHeader(root);
    setupStoryCarousel(root);
    setupHorizontalRails(root);
    setupInstagramLiveFeed(root);
    setupVibes(root);
    setupGallery(root);
    setupPurchaseChoices(root);
    setupStickyPdp(root);
    setupUsage(root);
    setupParityVideos(root);
    setupCartForms(root);
    setupDiscountCode(root);
    setupScrollSignal(root);
    setupReadSectionNavigation(root);
  });

  function setupImageShells(root) {
    // A below-fold placeholder should not repaint while nobody can see it.
    // Keep the existing animation as a fallback in browsers without observers.
    const visibility = "IntersectionObserver" in window
      ? new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          target.classList.toggle("is-image-in-view", isIntersecting);
        });
      }, { rootMargin: "80px" })
      : null;
    if (visibility) root.setAttribute("data-anou-image-observer", "");
    const tracked = new WeakSet();
    const markShell = (image) => {
      const shell = image.closest(".soft-image-shell");
      if (!shell || tracked.has(image)) return;
      tracked.add(image);
      const done = () => {
        shell.classList.add("is-loaded");
        visibility?.unobserve(shell);
      };
      if (image.complete && image.naturalWidth > 0) {
        done();
        return;
      }
      image.addEventListener("load", done, { once: true });
      image.addEventListener("error", done, { once: true });
      visibility?.observe(shell);
    };

    root.querySelectorAll(".soft-image-shell img").forEach(markShell);

    if (!("MutationObserver" in window)) return;
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.("img")) markShell(node);
          node.querySelectorAll?.(".soft-image-shell img").forEach(markShell);
        });
      });
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function setupMiniQuiz(root) {
    root.querySelectorAll("[data-anou-mini-quiz]").forEach((quiz) => {
      const start = quiz.querySelector("[data-anou-mini-quiz-start]");
      const mobileOpen = root.querySelector("[data-anou-mobile-formula-open]");
      const mobileWhatsapp = root.querySelector('.soft-mobile-whatsapp-launcher[data-mobile-action="formula"]');
      const step = quiz.querySelector("[data-anou-mini-quiz-step]");
      const results = Array.from(quiz.querySelectorAll("[data-anou-mini-quiz-result]"));
      let opener = start;
      const setOpen = (open) => {
        quiz.classList.toggle("is-started", open);
        quiz.classList.toggle("is-mobile-open", open);
        quiz.setAttribute("data-mobile-open", String(open));
        start?.setAttribute("aria-expanded", String(open));
        mobileOpen?.setAttribute("aria-expanded", String(open));
        mobileOpen?.setAttribute("data-mobile-open", String(open));
        mobileWhatsapp?.setAttribute("data-mobile-open", String(open));
      };
      const reset = (restoreFocus = false) => {
        setOpen(false);
        if (step) step.hidden = false;
        results.forEach((result) => { result.hidden = true; });
        if (restoreFocus) opener?.focus({ preventScroll:true });
      };

      start?.addEventListener("click", () => {
        opener = start;
        setOpen(true);
        quiz.querySelector("[data-anou-mini-quiz-close]")?.focus({ preventScroll:true });
      });
      mobileOpen?.addEventListener("click", () => {
        opener = mobileOpen;
        if (step) step.hidden = false;
        results.forEach((result) => { result.hidden = true; });
        setOpen(true);
        quiz.querySelector("[data-anou-mini-quiz-close]")?.focus({ preventScroll:true });
      });
      quiz.querySelector("[data-anou-mini-quiz-close]")?.addEventListener("click", () => reset(true));
      document.addEventListener("keydown", event => {
        if (event.key === "Escape" && quiz.dataset.mobileOpen === "true") reset(true);
      });
      document.addEventListener("pointerdown", event => {
        if (quiz.dataset.mobileOpen === "true" && !quiz.contains(event.target) && event.target !== mobileOpen && !mobileOpen?.contains(event.target)) reset();
      });
      new MutationObserver(() => {
        if (quiz.dataset.mobileOpen === "true" && (document.body.classList.contains("is-soft-bag-open") || document.body.dataset.bagOpen === "true")) reset();
      }).observe(document.body, { attributes:true, attributeFilter:["class","data-bag-open"] });
      quiz.querySelectorAll("[data-anou-mini-quiz-choice]").forEach((choice) => {
        choice.addEventListener("click", () => {
          const slug = choice.getAttribute("data-anou-mini-quiz-choice");
          if (step) step.hidden = true;
          results.forEach((result) => {
            result.hidden = result.getAttribute("data-anou-mini-quiz-result") !== slug;
          });
        });
      });
    });
  }

  function setupHeroSupport(root) {
    const dock = root.querySelector("[data-anou-support-dock]");
    const toggle = dock?.querySelector("[data-anou-support-toggle]");
    const sheet = dock?.querySelector("[data-anou-support-sheet]");
    const setExpanded = (open, restoreFocus = false) => {
      if (!dock || !toggle || !sheet) return;
      const changed = dock.dataset.expanded !== String(open);
      dock.dataset.expanded = String(open);
      toggle.setAttribute("aria-expanded", String(open));
      if (changed) dock.dispatchEvent(new Event("anou:support-change"));
      if (open) sheet.querySelector("a,button")?.focus({ preventScroll: true });
      else if (restoreFocus) toggle.focus({ preventScroll: true });
    };
    toggle?.addEventListener("click", () => setExpanded(true));
    sheet?.querySelector("[data-anou-support-close]")?.addEventListener("click", () => setExpanded(false, true));
    if (dock) {
      document.addEventListener("keydown", event => {
        if (event.key === "Escape" && dock.dataset.expanded === "true") setExpanded(false, true);
      });
      document.addEventListener("pointerdown", event => {
        if (!dock.contains(event.target)) setExpanded(false);
      });
    }
    const hero = root.querySelector(".soft-landing-hero, .soft-product-hero-panel, .soft-shop-product-row");
    const heroQuiz = root.querySelector(".soft-landing-hero .soft-hero-quiz-cta");
    const purchase = root.querySelector(".soft-purchase-control");
    const pronunciation = root.querySelector("[data-pronunciation-launcher]");
    const pdpPurchase = root.querySelector("#soft-pdp-buy .soft-purchase-control");
    const mobile = matchMedia("(max-width: 960px)");
    const tabletFilm = matchMedia("(min-width: 961px) and (max-width: 1180px)");
    let frame = 0;
    let previousPassed;
    let previousBottom = "";
    let pdpOutside;
    let pdpScrollPosition;
    let pdpScrolling = false;
    let scrollIdleTimer = 0;
    const cancelScrollIdle = () => {
      if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
      scrollIdleTimer = 0;
      pdpScrolling = false;
    };
    const scrollPosition = () => ({ y: window.scrollY || 0, top: window.visualViewport?.offsetTop || 0 });
    const onScroll = () => {
      if (mobile.matches) {
        const next = scrollPosition();
        if (pdpScrollPosition && (Math.abs(next.y - pdpScrollPosition.y) >= 2 || Math.abs(next.top - pdpScrollPosition.top) >= 2)) {
          // Hide while the page moves, then measure the current viewport once
          // it has rested. Inline content does not control PDP dock visibility.
          cancelScrollIdle();
          pdpScrolling = true;
          scrollIdleTimer = setTimeout(() => {
            scrollIdleTimer = 0;
            pdpScrolling = false;
            schedule();
          }, 220);
        }
        pdpScrollPosition = next;
      }
      schedule();
    };
    const publishPlacement = (bottom, obstructed, purchaseVisible) => {
      const blocked = String(obstructed);
      const value = `${bottom}px`;
      let changed = document.body.dataset.softSupportObstructed !== blocked;
      if (changed) document.body.dataset.softSupportObstructed = blocked;
      if (value !== previousBottom) {
        document.body.style.setProperty("--soft-support-bottom", value);
        previousBottom = value;
        if (dock) dock.dataset.supportBottom = value;
        changed = true;
      }
      if (dock && purchaseVisible !== undefined && dock.dataset.pdpPurchaseVisible !== String(purchaseVisible)) {
        dock.dataset.pdpPurchaseVisible = String(purchaseVisible);
        changed = true;
      }
      if (changed && dock) dock.dispatchEvent(new Event("anou:support-placement"));
    };
    const measure = () => {
      frame = 0;
      let pronunciationBlocked = !mobile.matches && !tabletFilm.matches ? "false" : undefined;
      if (!mobile.matches && dock?.dataset.expanded === "true") setExpanded(false);
      const heroBottom = mobile.matches && hero ? hero.getBoundingClientRect().bottom : null;
      const passed = heroBottom === null || (previousPassed === undefined ? heroBottom < 0 :
        previousPassed ? heroBottom < 12 : heroBottom < -12);
      const viewport = window.visualViewport;
      const magnified = String((viewport?.scale || 1) > 1.05);
      // Publish viewport flags with placement, after every layout/style read.
      // These flags style the fixed controls; writing them mid-measure forces
      // the next geometry read to recalculate the page again.
      const publishViewport = (inlineVisible) => {
        if (pronunciationBlocked !== undefined && document.body.dataset.softPronunciationBlocked !== pronunciationBlocked) document.body.dataset.softPronunciationBlocked = pronunciationBlocked;
        if (passed !== previousPassed) {
          document.body.dataset.softHeroPassed = String(passed);
          previousPassed = passed;
        }
        if (document.body.dataset.softSupportMagnified !== magnified) document.body.dataset.softSupportMagnified = magnified;
        if (inlineVisible !== undefined && document.body.dataset.softInlineActionVisible !== inlineVisible) document.body.dataset.softInlineActionVisible = inlineVisible;
      };
      // Portrait-style PDPs continue past the mobile navigation breakpoint.
      // The film alone yields to real controls through that tablet layout;
      // WhatsApp and the native purchase dock keep their existing ownership.
      if ((mobile.matches || tabletFilm.matches) && pronunciation && !pronunciation.hidden && !pdpScrolling) {
        const tile = pronunciation.getBoundingClientRect();
        const videoBlocked = [...root.querySelectorAll('.soft-hero-actions a, .soft-purchase-control, .soft-routine-add, .soft-pdp-hero-proof-cta, .soft-ingredient-buy-cta, .soft-ingredient-view-cta, .soft-discovery-add, [data-comparison-select], [data-comparison-range], [data-anou-usage-tab], .soft-results-link, .soft-footer a, .soft-footer p, .site-footer a, .site-footer p')].some(action => {
          const bounds = action.getBoundingClientRect();
          return bounds.width > 0 && bounds.height > 0 && bounds.bottom > tile.top && bounds.top < tile.bottom && bounds.right > tile.left && bounds.left < tile.right;
        });
        pronunciationBlocked = String(videoBlocked);
      }
      if (!mobile.matches) {
        // The mobile dock has no desktop collision geometry. Reset hysteresis
        // so a return to mobile measures the new layout without stale bounds.
        pdpOutside = undefined;
        pdpScrollPosition = undefined;
        cancelScrollIdle();
        publishViewport(pdpPurchase && dock ? undefined : "false");
        publishPlacement(14, Boolean(pdpPurchase && dock), pdpPurchase && dock ? false : undefined);
        return;
      }
      const safeArea = dock ? Number.parseFloat(window.getComputedStyle?.(dock)?.getPropertyValue("--soft-support-safe-area")) || 0 : 0;
      const height = (viewport?.height || innerHeight) - safeArea;
      const viewportTop = viewport?.offsetTop || 0;
      const bottomEdge = viewportTop + height;
      const top = Math.max(viewportTop, root.querySelector(".soft-mobile-topbar")?.getBoundingClientRect().bottom || 0);
      const focusedInput = document.activeElement?.matches?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])');
      const modalOpen = [...(document.querySelectorAll?.('dialog[open], [aria-modal="true"]') || [])].some(element => {
        if (element.closest?.('[hidden], [inert], [aria-hidden="true"]')) return false;
        const style = window.getComputedStyle?.(element);
        return style?.visibility !== "hidden" && style?.visibility !== "collapse" && element.getClientRects().length > 0;
      });
      const blocked = magnified === "true" || (viewport?.height || innerHeight) <= 420 || focusedInput ||
        document.body.classList?.contains("is-soft-bag-open") || document.body.dataset.bagOpen === "true" ||
        !!root.querySelector(".is-menu-open, .is-mobile-open") || modalOpen;
      if (pdpPurchase && dock) {
        if (!pdpScrollPosition) pdpScrollPosition = scrollPosition();
        const rect = pdpPurchase.getBoundingClientRect();
        // A sliver of the native action is not a usable purchase control.
        // Keep its floating counterpart until the whole action is visible.
        const mainVisible = rect.width > 0 && rect.height > 0 && rect.top >= top && rect.bottom <= bottomEdge;
        if (mainVisible) pdpOutside = false;
        else if (pdpOutside === undefined || rect.top < top - 4 || rect.bottom > bottomEdge + 4) pdpOutside = true;
        const unavailable = Boolean(blocked || pdpScrolling);
        const purchaseVisible = !unavailable && (dock.dataset.expanded === "true" || pdpOutside);
        publishViewport();
        publishPlacement(14, unavailable, purchaseVisible);
        return;
      }
      // Both corners stay at one fixed edge when the page rests. Content
      // passing behind them does not move the controls. Only Home's actual
      // quiz action suppresses its duplicate Formula action, never WhatsApp.
      if (!pdpScrollPosition) pdpScrollPosition = scrollPosition();
      const quizRect = heroQuiz?.getBoundingClientRect();
      const quizVisible = quizRect && quizRect.width > 0 && quizRect.height > 0 && quizRect.bottom > top && quizRect.top < bottomEdge;
      publishViewport(String(Boolean(blocked || pdpScrolling || quizVisible)));
      publishPlacement(14, Boolean(blocked || pdpScrolling));
    };
    const schedule = () => { if ((mobile.matches || tabletFilm.matches) && !frame) frame = requestAnimationFrame(measure); };
    const observer = new ResizeObserver(schedule);
    if (hero) observer.observe(hero);
    if (purchase) observer.observe(purchase);
    if (heroQuiz) observer.observe(heroQuiz);
    if (pronunciation) observer.observe(pronunciation);
    const formula = root.querySelector("[data-anou-formula-page]");
    if (formula) observer.observe(formula);
    addEventListener("scroll", onScroll, { passive: true });
    addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", onScroll);
    addEventListener("pagehide", () => {
      cancelScrollIdle();
      pdpScrollPosition = undefined;
      pdpOutside = undefined;
      if (frame) window.cancelAnimationFrame?.(frame);
      frame = 0;
    });
    addEventListener("pageshow", schedule);
    document.addEventListener("focusin", schedule);
    document.addEventListener("focusout", schedule);
    document.addEventListener("toggle", schedule, true);
    dock?.addEventListener("anou:support-change", schedule);
    if (typeof MutationObserver !== "undefined") {
      const stateObserver = new MutationObserver(schedule);
      stateObserver.observe(document.body, { attributes: true, attributeFilter: ["class", "data-bag-open"] });
      stateObserver.observe(root, { attributes: true, attributeFilter: ["class"] });
      const topbar = root.querySelector(".soft-mobile-topbar");
      if (topbar) stateObserver.observe(topbar, { attributes: true, attributeFilter: ["class"] });
      const miniQuiz = root.querySelector(".soft-rail-quiz");
      if (miniQuiz) stateObserver.observe(miniQuiz, { attributes: true, attributeFilter: ["class"] });
    }
    mobile.addEventListener("change", measure);
    tabletFilm.addEventListener("change", measure);
    measure();
  }

  function setupFormulaDock(root) {
    const quiz = root.querySelector("[data-anou-formula-page]");
    const dock = root.querySelector("[data-anou-quiz-dock]");
    const action = dock?.querySelector("[data-anou-quiz-dock-action]");
    if (!quiz || !dock || !action) return;
    const mobile = matchMedia("(max-width:960px) and (min-height:421px)");
    let target = null;
    let frame = 0;
    const update = () => {
      frame = 0;
      const step = quiz.querySelector("[data-anou-formula-step]:not([hidden])");
      const result = quiz.querySelector("[data-anou-formula-result]:not([hidden])");
      target = step?.querySelector("[data-anou-formula-continue]") || result?.querySelector("[data-anou-formula-result-cta]");
      const rect = result && target?.getBoundingClientRect();
      const height = window.visualViewport?.height || innerHeight;
      const nearbyAction = result && [...root.querySelectorAll(".soft-formula-result-actions a, .soft-quiz-reset, .soft-footer a")].some(element => {
        const bounds = element.getBoundingClientRect();
        return bounds.height > 0 && bounds.bottom > height - 88 && bounds.top < height;
      });
      const visible = mobile.matches && !!target && (!rect || !nearbyAction && (rect.bottom <= 72 || rect.top >= height - 88));
      action.hidden = !visible;
      dock.dataset.actionVisible = String(visible);
      quiz.toggleAttribute("data-anou-formula-docked", mobile.matches);
      if (!target) return;
      const text = (step ? target.textContent.trim() : `${target.querySelector('[data-anou-add-label]')?.textContent || 'Add to bag'} · ${target.querySelector('[data-anou-formula-result-price]')?.textContent || ''}`).replace(/₹\s+/g, '₹');
      if (action.textContent !== text) action.textContent = text;
      action.disabled = target.disabled;
      const name = result?.querySelector("[data-anou-formula-result-image]")?.dataset.productName;
      action.setAttribute("aria-label", name ? `${text} — ${name}` : text);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    // Delegate to the current native control: one step owner, one product form,
    // and no copied variant, price, recommendation or cart-request logic.
    action.addEventListener("click", () => {
      update();
      if (target && !target.disabled) {
        const previousStep = target.closest?.("[data-anou-formula-step]");
        target.click();
        const nextStep = previousStep && quiz.querySelector("[data-anou-formula-step]:not([hidden])");
        if (nextStep && nextStep !== previousStep) {
          const heading = nextStep.querySelector("h2");
          if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll:true }); }
          const inset = Math.max(0, root.querySelector(".soft-mobile-topbar")?.getBoundingClientRect().bottom || 0) + 12;
          scrollTo({ top:scrollY + nextStep.getBoundingClientRect().top - inset, behavior:"instant" });
        }
      }
      schedule();
    });
    new MutationObserver(schedule).observe(quiz, { subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:["hidden","disabled","class"] });
    addEventListener("scroll", schedule, { passive:true });
    addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    mobile.addEventListener("change", update);
    update();
  }

  function setupFormulaPage(root) {
    window.AnouFormula?.mount(root.querySelector("[data-anou-formula-page]"));
  }

  function setupMenus(root) {
    const panel = root.querySelector(".soft-left-panel");
    const topbar = root.querySelector(".soft-mobile-topbar");
    const mobileMenu = root.querySelector("[data-anou-mobile-menu]");
    const mobileToggle = root.querySelector("[data-anou-menu-toggle]");
    const leftMenu = root.querySelector("[data-anou-left-menu]");
    const railToggle = root.querySelector("[data-anou-rail-toggle]");
    const railMenu = leftMenu?.querySelector(".soft-menu-popover");
    const railActions = root.querySelectorAll(".soft-left-panel > .soft-rail-quiz, .soft-left-panel > .soft-support-card, .soft-product-summary");
    const railActionInert = new Map([...railActions].map(action => [action, action.hasAttribute("inert")]));

    const setMobileOpen = (open) => {
      if (!mobileMenu || !mobileToggle) return;
      mobileToggle.setAttribute("aria-expanded", String(open));
      mobileToggle.setAttribute("aria-label", open ? "Close navigation" : "Open navigation");
      mobileMenu.setAttribute("aria-hidden", String(!open));
      mobileMenu.toggleAttribute("inert", !open);
      topbar?.classList.toggle("is-menu-open", open);
      panel?.classList.toggle("is-menu-open", open);
      root.querySelectorAll(".soft-rail-quiz, .soft-mobile-whatsapp-launcher, .soft-mobile-formula-cta-fixed").forEach((action) => {
        action.setAttribute("data-menu-open", String(open));
      });
    };
    const setRailOpen = (open) => {
      leftMenu?.classList.toggle("is-menu-open", open);
      panel?.classList.toggle("is-rail-menu-open", open);
      railToggle?.setAttribute("aria-expanded", String(open));
      railToggle?.setAttribute("aria-label", open ? "More — hide full navigation" : "More — show full navigation");
      railMenu?.setAttribute("aria-hidden", String(!open));
      railMenu?.toggleAttribute("inert", !open);
      railActions.forEach(action => action.toggleAttribute("inert", open || railActionInert.get(action)));
    };

    mobileToggle?.addEventListener("click", () => {
      const open = mobileToggle.getAttribute("aria-expanded") !== "true";
      setMobileOpen(open);
      if (open) mobileMenu.querySelector("[data-anou-menu-close], a")?.focus({ preventScroll:true });
    });
    mobileMenu?.querySelectorAll("[data-anou-menu-close], a").forEach((item) => item.addEventListener("click", () => {
      setMobileOpen(false);
      if (item.hasAttribute("data-anou-menu-close")) mobileToggle?.focus({ preventScroll:true });
    }));
    railToggle?.addEventListener("click", () => {
      setRailOpen(railToggle.getAttribute("aria-expanded") !== "true");
    });
    leftMenu?.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => {
      setRailOpen(false);
    }));
    document.addEventListener("pointerdown", (event) => {
      if (railToggle?.getAttribute("aria-expanded") === "true" && !leftMenu.contains(event.target)) setRailOpen(false);
      if (mobileToggle?.getAttribute("aria-expanded") === "true" && !topbar.contains(event.target)) setMobileOpen(false);
    });
    leftMenu?.addEventListener("keydown", event => {
      if (event.target === railToggle && event.key === "ArrowDown") {
        event.preventDefault();
        setRailOpen(true);
        railMenu?.querySelector("a,button")?.focus({ preventScroll:true });
      }
    });
    document.addEventListener("keydown", event => {
      if (event.key !== "Escape") return;
      if (mobileToggle?.getAttribute("aria-expanded") === "true") {
        setMobileOpen(false); mobileToggle.focus({ preventScroll:true });
      }
      if (railToggle?.getAttribute("aria-expanded") === "true") {
        setRailOpen(false); railToggle.focus({ preventScroll:true });
      }
    });
    const mobile = matchMedia("(max-width: 960px)");
    mobile.addEventListener("change", () => {
      const focusedInMenu = leftMenu?.contains(document.activeElement) || mobileMenu?.contains(document.activeElement);
      setRailOpen(false);
      setMobileOpen(false);
      if (focusedInMenu) (mobile.matches ? mobileToggle : railToggle)?.focus({ preventScroll:true });
    });
  }

  function setupMobileTopbar(root) {
    const topbar = root.querySelector(".soft-mobile-topbar");
    if (!topbar) return;

    const mobile = matchMedia("(max-width: 960px)");
    let frame = 0;

    const update = () => {
      frame = 0;
      const isScrolled = mobile.matches && !topbar.classList.contains("is-menu-open") && scrollY > 12;
      if (topbar.classList.contains("is-scrolled") !== isScrolled) {
        topbar.classList.toggle("is-scrolled", isScrolled);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    addEventListener("scroll", schedule, { passive: true });
    addEventListener("resize", schedule);
    mobile.addEventListener("change", schedule);
    root.querySelector("[data-anou-menu-toggle]")?.addEventListener("click", schedule);
    update();
  }

  function setupStandardHeader(root) {
    const trigger = root.querySelector("[data-anou-standard-menu-open]");
    const overlay = root.querySelector("[data-anou-standard-menu]");
    if (!trigger || !overlay) return;

    let previousOverflow = "";
    const close = overlay.querySelector(".nav-overlay-close");
    const setOpen = (open, restoreFocus = false) => {
      if (open === !overlay.hidden) return;
      if (open) previousOverflow = document.body.style.overflow;
      overlay.hidden = !open;
      overlay.classList.toggle("is-open", open);
      trigger.classList.toggle("is-open", open);
      trigger.setAttribute("aria-expanded", String(open));
      document.body.style.overflow = open ? "hidden" : previousOverflow;
      if (open) close?.focus({ preventScroll:true });
      else if (restoreFocus) trigger.focus({ preventScroll:true });
    };

    trigger.addEventListener("click", () => setOpen(true));
    overlay.querySelectorAll("[data-anou-standard-menu-close]").forEach((button) => {
      button.addEventListener("click", () => setOpen(false, true));
    });
    overlay.querySelector("[data-anou-standard-menu-expand]")?.addEventListener("click", (event) => {
      overlay.classList.remove("is-panel");
      overlay.classList.add("is-fullscreen");
      event.currentTarget.hidden = true;
      close?.focus({ preventScroll:true });
    });
    overlay.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => setOpen(false)));
    document.addEventListener("keydown", (event) => {
      if (overlay.hidden) return;
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false, true);
      } else if (event.key === "Tab") {
        const items = [...overlay.querySelectorAll('a[href],button:not([disabled]),[tabindex="0"]')]
          .filter(item => !item.closest('[hidden],[inert]') && item.getClientRects().length);
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !overlay.contains(document.activeElement))) {
          event.preventDefault(); last?.focus({ preventScroll:true });
        } else if (!event.shiftKey && (document.activeElement === last || !overlay.contains(document.activeElement))) {
          event.preventDefault(); first?.focus({ preventScroll:true });
        }
      }
    });
  }

  function setupStoryCarousel(root) {
    root.querySelectorAll("[data-anou-story-carousel]").forEach((carousel) => {
      const slides = Array.from(carousel.querySelectorAll("[data-anou-story-slide]"));
      const dots = Array.from(carousel.querySelectorAll("[data-anou-story-dot]"));
      if (slides.length < 2 || dots.length !== slides.length) return;
      let active = 0;
      let timer = 0;

      const select = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const selected = slideIndex === active;
          slide.classList.toggle("is-active", selected);
          slide.setAttribute("aria-hidden", String(!selected));
        });
        dots.forEach((dot, dotIndex) => {
          const selected = dotIndex === active;
          dot.classList.toggle("is-active", selected);
          dot.setAttribute("aria-pressed", String(selected));
        });
      };
      const start = () => {
        clearInterval(timer);
        if (!matchMedia("(prefers-reduced-motion: reduce)").matches) {
          timer = setInterval(() => select(active + 1), 5000);
        }
      };

      dots.forEach((dot, index) => dot.addEventListener("click", () => { select(index); start(); }));
      start();
    });
  }

  function setupHorizontalRails(root) {
    const initialStates = [];
    root.querySelectorAll("[data-anou-horizontal-section]").forEach((section) => {
      const track = section.querySelector("[data-anou-horizontal-track]");
      if (!track) return;
      const buttons = section.querySelectorAll("[data-anou-scroll]");
      // Native-scroll tracks and story tiles have no arrow state to measure.
      if (!buttons.length) return;
      const measure = () => ({
        max: Math.max(0, track.scrollWidth - track.clientWidth),
        left: track.scrollLeft,
      });
      const apply = ({ max, left }) => {
        buttons.forEach((button) => {
          const direction = Number(button.getAttribute("data-anou-scroll"));
          const disabled = direction < 0 ? left <= 2 : left >= max - 2;
          if (button.disabled !== disabled) button.disabled = disabled;
        });
      };
      const update = () => apply(measure());
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          track.scrollBy({ left: Number(button.getAttribute("data-anou-scroll")) * track.clientWidth * 0.76, behavior: "smooth" });
        });
      });
      track.addEventListener("scroll", update, { passive: true });
      initialStates.push({ apply, state: measure() });
    });
    initialStates.forEach(({ apply, state }) => apply(state));
  }

  function setupInstagramLiveFeed(root) {
    const shell = root.querySelector(".soft-instagram-live-shell");
    const feed = shell?.querySelector("#insta-feed");
    if (!shell || !feed) return;

    const isFeedPhoto = (img) =>
      img.parentElement?.classList.contains("instafeed-new-layout-item") && img.naturalWidth > 10;

    const updateReady = () => {
      const photos = Array.from(feed.querySelectorAll(".instafeed-new-layout-item > img"));
      const ready = photos.length > 0 && photos.every((img) => img.complete && isFeedPhoto(img));
      shell.classList.toggle("is-instagram-live-ready", ready);
      feed.inert = !ready;
      feed.setAttribute("aria-hidden", String(!ready));
    };

    const tinyGif = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
    const hydratedSources = new WeakMap();
    const observedPhotos = new WeakSet();
    const coverCorrections = new Map(Array.from(shell.querySelectorAll("[data-instagram-cover-id]"), (card) =>
      [card.getAttribute("data-instagram-cover-id"), { path: card.getAttribute("data-instagram-provider-path"), src: card.getAttribute("data-instagram-cover-src") }]));
    const imageDescriptions = new Map(Array.from(shell.querySelectorAll("[data-instagram-alt-id]"), (card) =>
      [card.dataset.instagramAltId, { key: card.dataset.instagramAltKey, src: card.dataset.instagramAltSrc, alt: card.dataset.instagramAlt }]));
    const altStates = new WeakMap();
    const describeImage = (img) => {
      const postId = img.parentElement?.querySelector("[data-instafeed-open-id]")?.getAttribute("data-instafeed-open-id")?.replace(/-insta-feed$/, "");
      let source;
      try { source = new URL(img.getAttribute("data-instafeed-src") || img.src); } catch { return; }
      if (source.protocol !== "https:") return;
      const identity = `${postId || ""}:${source.hostname}${source.pathname}`;
      let state = altStates.get(img);
      const apply = () => { if (img.alt !== state.alt) img.alt = state.alt; };
      if (state?.identity === identity) { apply(); return; }
      state = { identity, alt: "Anoū Instagram post thumbnail." };
      altStates.set(img, state);
      apply();
      const sameSource = (record) => {
        try { const saved = new URL(record.src); return saved.hostname === source.hostname && saved.pathname === source.pathname; } catch { return false; }
      };
      const record = imageDescriptions.get(postId) || [...imageDescriptions.values()].find(sameSource);
      if (!record?.alt) return;
      if (sameSource(record)) { state.alt = record.alt; apply(); return; }
      if (!postId || !/^[a-f0-9]{20}$/.test(record.key || "") || !window.crypto?.subtle || typeof TextEncoder === "undefined") return;
      // Same identity recipe as the server mirror: renewed URL signatures are
      // harmless, but a different thumbnail must never inherit the old image alt.
      window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(identity)).then((digest) => {
        if (altStates.get(img) !== state) return;
        const key = Array.from(new Uint8Array(digest), value => value.toString(16).padStart(2, "0")).join("").slice(0, 20);
        if (key === record.key) { state.alt = record.alt; apply(); }
      }).catch(() => { /* The neutral thumbnail description remains available. */ });
    };
    let allowHydrate = false;

    const isInstagramCdn = (value) =>
      /cdninstagram|scontent\.cdninstagram|instagram\.com\//i.test(value || "");

    const park = (img) => {
      const src = img.getAttribute("src") || "";
      if (!isInstagramCdn(src) || src === tinyGif) return;
      if (img.getAttribute("data-instafeed-src") !== src) img.setAttribute("data-instafeed-src", src);
      hydratedSources.delete(img);
      img.removeAttribute("data-anou-ig-hydrated");
      img.setAttribute("src", tinyGif);
    };

    const hydrate = (img) => {
      const url = img.getAttribute("data-instafeed-src");
      if (!url || hydratedSources.get(img) === url) return;
      hydratedSources.set(img, url);
      img.setAttribute("data-anou-ig-hydrated", "1");
      const current = img.getAttribute("src") || "";
      if (current !== url) img.src = url;
      if (img.complete) updateReady();
    };

    const correctCover = (img) => {
      const item = img.parentElement;
      const postId = item.querySelector("[data-instafeed-open-id]")?.getAttribute("data-instafeed-open-id")?.replace(/-insta-feed$/, "");
      const correction = coverCorrections.get(postId);
      if (!correction) return;
      const provider = item.getAttribute("data-instafeed-thumbnail");
      try {
        if (new URL(correction.src).hostname !== "cdn.shopify.com") return;
        if (new URL(provider).pathname !== correction.path) {
          if (img.getAttribute("data-instafeed-src") === correction.src) img.setAttribute("data-instafeed-src", provider);
          return;
        }
        if (img.getAttribute("data-instafeed-src") !== correction.src) img.setAttribute("data-instafeed-src", correction.src);
        if (img.getAttribute("src") !== correction.src) img.setAttribute("src", correction.src);
      } catch { /* Keep the provider image when correction metadata is invalid. */ }
    };

    const scan = () => {
      feed.querySelectorAll(".instafeed-new-layout-item > img").forEach((img) => {
        if (observedPhotos.has(img)) return;
        observedPhotos.add(img);
        img.addEventListener("load", updateReady);
        img.addEventListener("error", updateReady);
      });
      updateReady();
      if (!allowHydrate) {
        feed.querySelectorAll("img").forEach(park);
        feed.querySelectorAll(".instafeed-new-layout-item > img").forEach(describeImage);
        return;
      }
      feed.querySelectorAll(".instafeed-new-layout-item > img").forEach(correctCover);
      feed.querySelectorAll("img[data-instafeed-src]").forEach(hydrate);
      feed.querySelectorAll(".instafeed-new-layout-item > img").forEach(describeImage);
    };

    const observer = new MutationObserver(scan);
    observer.observe(feed, { childList: true, subtree: true, attributes: true, attributeFilter: ["src", "data-instafeed-src", "data-instafeed-thumbnail", "alt"] });

    const enableHydrate = () => {
      if (allowHydrate) return;
      allowHydrate = true;
      scan();
    };

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            enableHydrate();
            io.disconnect();
          }
        },
        { rootMargin: "480px 0px" },
      );
      io.observe(shell);
    } else {
      enableHydrate();
    }
    scan();
  }

  function setupVibes(root) {
    const section = root.querySelector("[data-anou-vibes]");
    const lines = Array.from(section?.querySelectorAll(".soft-vibes-line") ?? []);
    if (!section || !lines.length) return;
    let frame = 0;
    let active = -1;
    let transitionUntil = 0;
    let boundaryHoldDirection = 0;
    let releaseDirection = 0;
    let wheelAccumulator = 0;
    let wheelDirection = 0;
    let lastWheelAt = 0;
    const setActive = (nextActive) => {
      if (active === nextActive) return;
      active = nextActive;
      section.style.setProperty("--active-index", String(active));
      lines.forEach((line, index) => {
        line.classList.toggle("is-active", index === active);
        line.classList.toggle("is-before", index < active);
        line.classList.toggle("is-after", index > active);
        if (index === active) line.setAttribute("aria-current", "true");
        else line.removeAttribute("aria-current");
      });
    };
    const update = () => {
      frame = 0;
      const mobile = window.matchMedia("(max-width: 960px)").matches;
      if (mobile) {
        transitionUntil = boundaryHoldDirection = releaseDirection = wheelAccumulator = wheelDirection = 0;
        section.classList.remove("is-scroll-captured");
        section.dataset.anouVibesState = "idle";
        setActive(0);
        return;
      }
      const now = performance.now();
      if (now < transitionUntil) return;
      if (boundaryHoldDirection !== 0 && now - lastWheelAt <= 260) return;
      boundaryHoldDirection = 0;
      if (releaseDirection !== 0) {
        const sticky = section.querySelector(".soft-vibes-sticky");
        if (sticky) {
          const rect = section.getBoundingClientRect();
          const stickyTop = Number.parseFloat(window.getComputedStyle(sticky).top) || 0;
          const hasExited = releaseDirection > 0
            ? rect.bottom < window.innerHeight - 2
            : rect.top > stickyTop + 2;
          if (!hasExited) return;
        }
        releaseDirection = 0;
        section.classList.remove("is-scroll-captured");
      }
      const rect = section.getBoundingClientRect();
      const travel = Math.max(1, rect.height - window.innerHeight);
      const progress = Math.min(0.999, Math.max(0, -rect.top / travel));
      const edgeHold = 0.08;
      const stagedProgress = Math.min(
        0.999,
        Math.max(0, (progress - edgeHold) / (1 - edgeHold * 2)),
      );
      setActive(Math.min(lines.length - 1, Math.floor(stagedProgress * lines.length)));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const handleWheel = (event) => {
      const normalizedDeltaY = event.deltaMode === 1
        ? event.deltaY * 16
        : event.deltaMode === 2
          ? event.deltaY * window.innerHeight
          : event.deltaY;
      if (
        event.ctrlKey ||
        Math.abs(normalizedDeltaY) < 0.5 ||
        window.matchMedia("(max-width: 960px)").matches ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) return;

      const sectionRect = section.getBoundingClientRect();
      const sticky = section.querySelector(".soft-vibes-sticky");
      if (!sticky) return;
      const stickyTop = Number.parseFloat(window.getComputedStyle(sticky).top) || 0;
      const sectionTop = window.scrollY + sectionRect.top;
      const pinStart = sectionTop - stickyTop;
      const pinEnd = pinStart + Math.max(1, section.offsetHeight - sticky.offsetHeight);
      const currentY = window.scrollY;
      const projectedY = currentY + normalizedDeltaY;
      const direction = normalizedDeltaY > 0 ? 1 : -1;
      const approachingPin = direction > 0
        ? currentY < pinStart - 3 && projectedY >= pinStart - 2
        : currentY > pinEnd + 3 && projectedY <= pinEnd + 2;
      const now = performance.now();
      const wheelGap = now - lastWheelAt;

      if (approachingPin) {
        event.preventDefault();
        section.dataset.anouVibesState = "entry";
        window.scrollTo({ top: direction > 0 ? pinStart : pinEnd, behavior: "auto" });
        setActive(direction > 0 ? 0 : lines.length - 1);
        transitionUntil = now + 180;
        boundaryHoldDirection = 0;
        releaseDirection = 0;
        wheelAccumulator = 0;
        wheelDirection = direction;
        lastWheelAt = now;
        section.classList.add("is-scroll-captured");
        return;
      }

      const isWithinPin = currentY >= pinStart - 3 && currentY <= pinEnd + 3;
      if (!isWithinPin) {
        wheelAccumulator = 0;
        wheelDirection = 0;
        boundaryHoldDirection = 0;
        section.classList.remove("is-scroll-captured");
        section.dataset.anouVibesState = "idle";
        return;
      }

      if (releaseDirection !== 0) {
        if (releaseDirection === direction) return;
        releaseDirection = 0;
      }

      if (boundaryHoldDirection !== 0 && boundaryHoldDirection !== direction) {
        boundaryHoldDirection = 0;
      }
      if (boundaryHoldDirection === direction) {
        if (now >= transitionUntil) {
          boundaryHoldDirection = 0;
          releaseDirection = direction;
          lastWheelAt = now;
          section.classList.remove("is-scroll-captured");
          section.dataset.anouVibesState = "released";
          return;
        }
        event.preventDefault();
        lastWheelAt = now;
        section.dataset.anouVibesState = "boundary-hold";
        return;
      }

      const canStep = direction > 0 ? active < lines.length - 1 : active > 0;
      if (!canStep) {
        if (now >= transitionUntil) {
          releaseDirection = direction;
          lastWheelAt = now;
          section.classList.remove("is-scroll-captured");
          section.dataset.anouVibesState = "released";
          return;
        }
        event.preventDefault();
        boundaryHoldDirection = direction;
        wheelAccumulator = 0;
        wheelDirection = 0;
        lastWheelAt = now;
        section.dataset.anouVibesState = "boundary-hold";
        return;
      }

      event.preventDefault();
      section.classList.add("is-scroll-captured");
      lastWheelAt = now;
      if (now < transitionUntil) {
        section.dataset.anouVibesState = "transition";
        return;
      }

      if (wheelDirection !== direction || wheelGap > 260) {
        wheelAccumulator = 0;
        wheelDirection = direction;
      }
      wheelAccumulator += normalizedDeltaY;
      if (Math.abs(wheelAccumulator) < 28) {
        section.dataset.anouVibesState = "accumulating";
        return;
      }

      wheelAccumulator = 0;
      transitionUntil = now + 520;
      const nextIndex = Math.min(lines.length - 1, Math.max(0, active + direction));
      setActive(nextIndex);
      section.dataset.anouVibesState = `line-${nextIndex + 1}`;
      const reachedBoundary = direction > 0 ? nextIndex === lines.length - 1 : nextIndex === 0;
      boundaryHoldDirection = reachedBoundary ? direction : 0;
    };
    window.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    update();
  }

  function setupFooterMotion(root) {
    const nodes = Array.from(root.querySelectorAll(".soft-footer-wordmark-reveal"));
    if (!nodes.length) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const activeNodes = new Set();
    const scales = new WeakMap();
    let frame = 0;

    const scaleAt = (top) => {
      const viewportHeight = Math.max(1, window.innerHeight);
      const start = viewportHeight * 0.74;
      const end = viewportHeight * 0.26;
      const rawProgress = (start - top) / Math.max(1, start - end);
      const progress = Math.min(1, Math.max(0, rawProgress));
      const eased = progress * progress * (3 - 2 * progress);
      return (1 + (1 - eased) * 0.058).toFixed(4);
    };
    const setScale = (node, scale) => {
      if (scales.get(node) === scale) return;
      node.style.setProperty("--soft-footer-scroll-scale", scale);
      scales.set(node, scale);
    };
    const update = () => {
      frame = 0;
      if (reducedMotion.matches) {
        nodes.forEach((node) => setScale(node, "1"));
        return;
      }
      const measured = Array.from(activeNodes, (node) => [node, scaleAt(node.getBoundingClientRect().top)]);
      measured.forEach(([node, scale]) => setScale(node, scale));
    };
    const schedule = () => {
      if (!frame && activeNodes.size && !reducedMotion.matches) frame = window.requestAnimationFrame(update);
    };

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle("is-visible", entry.isIntersecting);
          });
        },
        { rootMargin: "0px 0px -12% 0px", threshold: 0.18 },
      );
      nodes.forEach((node) => observer.observe(node));
      // Use observer-provided geometry for entry, including restored scroll
      // positions. The lookahead prepares the scale before the footer appears.
      const proximity = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activeNodes.add(entry.target);
          else activeNodes.delete(entry.target);
          setScale(entry.target, reducedMotion.matches ? "1" : scaleAt(entry.boundingClientRect.top));
        });
      }, { rootMargin: "240px 0px" });
      nodes.forEach((node) => proximity.observe(node));
    } else {
      nodes.forEach((node) => {
        node.classList.add("is-visible");
        activeNodes.add(node);
      });
      schedule();
    }

    if (reducedMotion.matches) update();
    reducedMotion.addEventListener("change", () => {
      if (reducedMotion.matches) update();
      else schedule();
    });
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
  }

  function setupGallery(root) {
    root.querySelectorAll("[data-anou-gallery]").forEach((gallery) => {
      if (gallery.hasAttribute("data-anou-gallery-ready")) return;
      const main = gallery.querySelector("[data-anou-main-image]");
      const buttons = Array.from(gallery.querySelectorAll("[data-anou-gallery-thumb]"));
      const mainButton = gallery.querySelector(".soft-hero-main-image-button, .anou-soft-product__main-media");
      const video = gallery.querySelector('[data-anou-gallery-video]');
      const status = gallery.querySelector('[data-anou-gallery-status]');
      const thumbnailStrip = gallery.querySelector('[data-anou-gallery-thumbnails]');
      const thumbnailMore = gallery.querySelector('[data-anou-thumbnail-more]');
      const thumbnailPrevious = gallery.querySelector('[data-anou-thumbnail-previous]');
      if (!main || !buttons.length) return;
      gallery.setAttribute("data-anou-gallery-ready", "true");
      const clearImageCorners = () => {
        main.removeAttribute('data-anou-gallery-rounded');
        main.style.removeProperty('--soft-gallery-image-ratio');
      };
      const updateImageCorners = () => {
        const style = getComputedStyle(main);
        if (!main.naturalWidth || !main.naturalHeight || style.objectFit !== 'contain' || style.objectPosition !== '50% 50%') {
          clearImageCorners();
          return;
        }
        main.style.setProperty('--soft-gallery-image-ratio', String(main.naturalWidth / main.naturalHeight));
        main.setAttribute('data-anou-gallery-rounded', '');
      };
      main.addEventListener('load', updateImageCorners);
      main.addEventListener('error', clearImageCorners);
      if (main.complete) updateImageCorners();
      if (thumbnailStrip && thumbnailMore && thumbnailPrevious) {
        const updateThumbnailOverflow = () => {
          const vertical = getComputedStyle(thumbnailStrip).flexDirection.startsWith('column');
          const maximum = Math.max(0, vertical ? thumbnailStrip.scrollHeight - thumbnailStrip.clientHeight : thumbnailStrip.scrollWidth - thumbnailStrip.clientWidth);
          const position = Math.max(0, Math.min(maximum, vertical ? thumbnailStrip.scrollTop : thumbnailStrip.scrollLeft));
          const remaining = maximum - position > 4;
          const previous = position > 4;
          const focusedControl = document.activeElement;
          const moveFocus = (focusedControl === thumbnailMore && !remaining) || (focusedControl === thumbnailPrevious && !previous);
          thumbnailMore.hidden = !remaining;
          thumbnailPrevious.hidden = !previous;
          thumbnailMore.closest('.soft-thumbnail-browser')?.classList.toggle('has-more', remaining);
          thumbnailPrevious.closest('.soft-thumbnail-browser')?.classList.toggle('has-previous', previous);
          [thumbnailMore, thumbnailPrevious].forEach(button => button.classList.toggle('is-vertical', vertical));
          if (moveFocus) {
            const target = remaining ? thumbnailMore : previous ? thumbnailPrevious : buttons.find(button => button.classList.contains('is-active')) || buttons[0];
            target.focus({ preventScroll:true });
          }
        };
        const scrollThumbnails = (direction) => {
          const vertical = getComputedStyle(thumbnailStrip).flexDirection.startsWith('column');
          thumbnailStrip.scrollBy({ left: vertical ? 0 : direction * thumbnailStrip.clientWidth * .72, top: vertical ? direction * thumbnailStrip.clientHeight * .72 : 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        };
        thumbnailMore.addEventListener('click', () => scrollThumbnails(1));
        thumbnailPrevious.addEventListener('click', () => scrollThumbnails(-1));
        thumbnailStrip.addEventListener('scroll', updateThumbnailOverflow, { passive:true });
        if (typeof ResizeObserver !== 'undefined') new ResizeObserver(updateThumbnailOverflow).observe(thumbnailStrip);
        updateThumbnailOverflow();
      }
      let active = Math.max(0, buttons.findIndex((button) => button.classList.contains("is-active")));
      let requested = active;
      let startX = null;
      let startY = null;
      let pointerId = null;
      let blockedUntil = 0;
      const pointers = new Set();
      const magnified = () => (window.visualViewport?.scale || 1) > 1.01;
      let suppressNextClick = false;
      let suppressClickTimer = 0;
      let imageRequest = 0;
      const preloads = new Map();
      const normalizeIndex = (index) => (index + buttons.length) % buttons.length;
      const preload = (index) => {
        const normalizedIndex = normalizeIndex(index);
        if (preloads.has(normalizedIndex)) return preloads.get(normalizedIndex);
        const button = buttons[normalizedIndex];
        const source = button.getAttribute("data-full") || "";
        const sourceSet = button.getAttribute("data-full-srcset") || "";
        const loader = new Image();
        const ready = new Promise((resolve) => {
          const finish = () => {
            if (typeof loader.decode !== "function") return resolve();
            loader.decode().catch(() => undefined).then(resolve);
          };
          loader.addEventListener("load", finish, { once: true });
          loader.addEventListener("error", resolve, { once: true });
          loader.sizes = main.getAttribute("sizes") || "(max-width: 960px) calc(100vw - 40px), 42vw";
          if (sourceSet) loader.srcset = sourceSet;
          loader.src = source;
          if (loader.complete) finish();
        });
        preloads.set(normalizedIndex, ready);
        return ready;
      };
      const warmAdjacentImages = () => {
        if (magnified()) return;
        preload(active + 1);
        preload(active - 1);
      };
      const show = (index) => {
        const nextIndex = normalizeIndex(index);
        requested = nextIndex;
        const request = ++imageRequest;
        preload(nextIndex).then(() => {
          if (request !== imageRequest) return;
          active = nextIndex;
          const button = buttons[active];
          const slotClass = Array.from(button.classList).find((name) => /^is-hero-slot-\d+$/.test(name));
          const sourceSet = button.getAttribute("data-full-srcset") || "";
          clearImageCorners();
          if (sourceSet) main.setAttribute("srcset", sourceSet);
          else main.removeAttribute("srcset");
          main.setAttribute("src", button.getAttribute("data-full") || "");
          main.setAttribute("alt", button.getAttribute("data-alt") || "");
          const videoSource = button.getAttribute('data-video');
          if (video) {
            video.pause();
            video.hidden = !videoSource;
            if (videoSource) {
              video.poster = button.getAttribute('data-full') || '';
              if (video.getAttribute('src') !== videoSource) video.src = videoSource;
            } else {
              video.removeAttribute('src');
              video.load();
            }
          }
          if (mainButton) {
            mainButton.hidden = Boolean(videoSource);
            if (mainButton.hasAttribute('data-pdp-viewer-hero')) {
              mainButton.setAttribute('href', button.getAttribute('data-full'));
            }
          }
          if (status) status.textContent = `${videoSource ? 'Video' : 'Image'} ${active + 1} of ${buttons.length}`;
          const photoSetting = button.getAttribute("data-anou-photo-setting");
          if (photoSetting) main.setAttribute("data-anou-owner-image", photoSetting);
          else main.removeAttribute("data-anou-owner-image");
          const photoPosition = button.getAttribute("data-full-position");
          if (photoSetting && photoPosition) {
            main.style.setProperty("--anou-photo-position", photoPosition);
            main.style.setProperty("object-position", "var(--anou-photo-position)", "important");
          } else {
            main.style.removeProperty("--anou-photo-position");
            main.style.removeProperty("object-position");
          }
          if (mainButton) {
            Array.from(mainButton.classList)
              .filter((name) => /^is-hero-slot-\d+$/.test(name))
              .forEach((name) => mainButton.classList.remove(name));
            if (slotClass) mainButton.classList.add(slotClass);
          }
          buttons.forEach((item, itemIndex) => {
            item.classList.toggle("is-active", itemIndex === active);
            item.setAttribute("aria-pressed", String(itemIndex === active));
          });
          if (thumbnailStrip) {
            const stripBounds = thumbnailStrip.getBoundingClientRect();
            const thumbBounds = button.getBoundingClientRect();
            const outsideX = thumbBounds.left < stripBounds.left || thumbBounds.right > stripBounds.right;
            const outsideY = thumbBounds.top < stripBounds.top || thumbBounds.bottom > stripBounds.bottom;
            if (outsideX || outsideY) thumbnailStrip.scrollTo({
              left: outsideX ? thumbnailStrip.scrollLeft + thumbBounds.left - stripBounds.left - (stripBounds.width - thumbBounds.width) / 2 : thumbnailStrip.scrollLeft,
              top: outsideY ? thumbnailStrip.scrollTop + thumbBounds.top - stripBounds.top - (stripBounds.height - thumbBounds.height) / 2 : thumbnailStrip.scrollTop,
              behavior: 'auto',
            });
          }
          if (gallery.contains(document.activeElement) && document.activeElement?.matches("[data-anou-gallery-thumb]")) {
            button.focus({ preventScroll: true });
          }
          warmAdjacentImages();
        });
      };
      // Defer secondary full-sized images until shoppers interact with the gallery.
      mainButton?.addEventListener("pointerenter", warmAdjacentImages, { once: true, passive: true });
      mainButton?.addEventListener("focus", warmAdjacentImages, { once: true });
      buttons.forEach((button, index) => button.addEventListener("click", () => show(index)));
      gallery.querySelectorAll('[data-anou-gallery-direction]').forEach(button => button.addEventListener('click', () => {
        if (!magnified()) show(requested + Number(button.dataset.anouGalleryDirection));
      }));
      if (video && 'IntersectionObserver' in window) {
        new IntersectionObserver(entries => { if (!entries[0].isIntersecting) video.pause(); }).observe(gallery);
      }
      mainButton?.addEventListener("click", (event) => {
        if (magnified() || pointers.size > 1 || Date.now() < blockedUntil || suppressNextClick) {
          suppressNextClick = false;
          clearTimeout(suppressClickTimer);
          event.preventDefault();
          return;
        }
        // The PDP viewer handles the selected still. Legacy galleries advance here.
        if (!mainButton.hasAttribute('data-pdp-viewer-hero')) show(requested + 1);
      });
      // Page-level arrows also work before the shopper has clicked a photo.
      // Leave form fields, other controls, dialogs and magnification to the browser.
      document.addEventListener("keydown", (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || magnified()) return;
        const target = event.target;
        if (!(target instanceof Element) || target.closest('input, textarea, select, video, [contenteditable]:not([contenteditable="false"]), [role="slider"], [role="spinbutton"]')) return;
        if (!gallery.contains(target) && target.closest('a[href], button, summary, [role="button"], [role="tab"], [role="listbox"], [role="menu"], [tabindex]:not([tabindex="-1"])')) return;
        if (document.body.classList.contains('is-soft-bag-open') || document.body.dataset.bagOpen === 'true' || root.querySelector('.is-menu-open, .is-mobile-open')) return;
        if ([...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(element => {
          if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
          const style = window.getComputedStyle(element);
          return style.visibility !== 'hidden' && style.visibility !== 'collapse' && element.getClientRects().length > 0;
        })) return;
        const bounds = gallery.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
        event.preventDefault();
        const focusedIndex = buttons.indexOf(target.closest('[data-anou-gallery-thumb]'));
        const cursor = requested !== active || focusedIndex < 0 ? requested : focusedIndex;
        show(cursor + (event.key === 'ArrowRight' ? 1 : -1));
      });
      document.addEventListener("pointerdown", (event) => {
        if (event.pointerType !== "touch") return;
        // The open photo viewer owns its pinch; leave the inline gallery ready on return.
        if (!pointers.size && event.target?.closest?.('[data-pdp-viewer][open]')) return;
        pointers.add(event.pointerId);
        if (pointers.size > 1) { startX = null; pointerId = null; blockedUntil = Date.now() + 600; }
      }, { capture:true, passive:true });
      const release = event => { pointers.delete(event.pointerId); };
      document.addEventListener("pointerup", release, { capture:true, passive:true });
      document.addEventListener("pointercancel", release, { capture:true, passive:true });
      mainButton?.addEventListener("pointerdown", (event) => {
        if (!event.isPrimary || magnified() || pointers.size > 1) return;
        startX = event.clientX; startY = event.clientY; pointerId = event.pointerId;
      }, { passive:true });
      mainButton?.addEventListener("pointerup", (event) => {
        const initialX = startX;
        startX = null;
        if (initialX === null || event.pointerId !== pointerId || magnified() || Date.now() < blockedUntil || Math.abs(event.clientX - initialX) < 42 || Math.abs(event.clientX - initialX) < Math.abs(event.clientY - startY)) return;
        suppressNextClick = true;
        clearTimeout(suppressClickTimer);
        suppressClickTimer = window.setTimeout(() => { suppressNextClick = false; }, 420);
        show(requested + (event.clientX < initialX ? 1 : -1));
      });
      mainButton?.addEventListener("pointercancel", () => { startX = null; pointerId = null; blockedUntil = Date.now()+600; });
      let wheelAt = 0;
      mainButton?.addEventListener("wheel", event => {
        if(event.ctrlKey || event.metaKey || magnified() || Math.abs(event.deltaX) < 12 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        if(Date.now()-wheelAt < 400) return; wheelAt=Date.now(); show(requested+(event.deltaX > 0 ? 1 : -1));
      }, { passive:true });
    });
  }

  function setupCartForms(root) {
    const bridge = window.AnouSoftBag = window.AnouSoftBag || { pending: [] };
    let initialized = false;
    const intentEvents = ["pointerover", "focusin", "click"];
    const initialize = () => {
      if (initialized) return;
      initialized = true;
      intentEvents.forEach((name) => root.removeEventListener(name, onIntent, true));
      setupBagDrawer(root);
    };
    const onIntent = (event) => {
      if (event.target instanceof Element && event.target.closest(
        '[data-anou-bag-link], form.soft-shopify-product-form button[type="submit"]',
      )) initialize();
    };
    bridge.activate = initialize;
    intentEvents.forEach((name) => root.addEventListener(name, onIntent, true));
    if (bridge.pending?.length || new URLSearchParams(window.location.search).get("open_bag") === "1") {
      initialize();
    }
  }

  function setupDiscountCode(root) {
    const input = root.querySelector("[data-anou-discount-code]");
    const button = root.querySelector("[data-anou-discount-apply]");
    const status = root.querySelector("[data-anou-discount-status]");
    const statusMessage = status?.querySelector("[data-anou-discount-message]");
    const removeButton = status?.querySelector("[data-anou-discount-remove]");
    const discountSummary = root.querySelector("[data-anou-discount-summary]");
    if (!input || !button) return;
    const storageKey = "anou-track-b-discount-attempt";
    let attemptedCode = "";
    try {
      attemptedCode = window.sessionStorage.getItem(storageKey) || "";
    } catch {
      attemptedCode = "";
    }
    const routesRoot = window.Shopify?.routes?.root || "/";
    const cartUrl = `${routesRoot}cart.js`;
    const cartUpdateUrl = `${routesRoot}cart/update.js`;
    const applicableCodes = (cart) => (cart?.discount_codes || [])
      .filter((discount) => discount?.applicable)
      .map((discount) => discount.code)
      .filter(Boolean);
    const showApplied = (code) => {
      if (!status || !code) return;
      status.hidden = false;
      status.classList.add("is-paid");
      status.classList.remove("is-failed");
      if (statusMessage) statusMessage.textContent = `${code} applied.`;
      if (removeButton) removeButton.hidden = false;
      const summaryText = discountSummary?.textContent.trim() || "";
      if (discountSummary && summaryText && !summaryText.toLowerCase().includes(code.toLowerCase())) {
        discountSummary.textContent = `${code} · ${summaryText}`;
      }
    };
    const showFailed = (code, message = `${code} could not be applied.`) => {
      if (!status) return;
      status.hidden = false;
      status.classList.remove("is-paid");
      status.classList.add("is-failed");
      if (statusMessage) statusMessage.textContent = message;
      if (removeButton) removeButton.hidden = true;
      if (code) input.value = code;
    };
    const hydrateDiscountState = async () => {
      try {
        const response = await fetch(cartUrl, {
          headers: { "Cache-Control": "no-cache", "X-Requested-With": "XMLHttpRequest" },
        });
        if (!response.ok) throw new Error("Unable to read discount");
        const cart = await response.json();
        const [appliedCode] = applicableCodes(cart);
        if (appliedCode) showApplied(appliedCode);
        else if (attemptedCode) showFailed(attemptedCode);
      } catch {
        const liquidCodes = (status?.dataset.anouAppliedDiscounts || "")
          .split("|")
          .map((code) => code.trim())
          .filter(Boolean);
        if (liquidCodes[0]) showApplied(liquidCodes[0]);
        else if (attemptedCode) showFailed(attemptedCode);
      } finally {
        try {
          window.sessionStorage.removeItem(storageKey);
        } catch {
          // The Ajax cart remains authoritative when session storage is unavailable.
        }
      }
    };
    const updateDiscount = async (code) => {
      const response = await fetch(cartUpdateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ discount: code }),
      });
      if (!response.ok) throw new Error("Unable to update discount");
      return response.json();
    };
    const apply = async () => {
      const code = input.value.trim();
      if (!code) {
        input.focus();
        return;
      }
      button.disabled = true;
      try {
        window.sessionStorage.setItem(storageKey, code);
      } catch {
        // Shopify still validates the code when session storage is unavailable.
      }
      try {
        const updatedCart = await updateDiscount(code);
        if (!applicableCodes(updatedCart).some((appliedCode) => appliedCode.toLowerCase() === code.toLowerCase())) {
          try {
            window.sessionStorage.removeItem(storageKey);
          } catch {
            // The returned cart response is authoritative.
          }
          showFailed(code);
          button.disabled = false;
          return;
        }
        window.location.reload();
      } catch {
        showFailed(code, "That discount could not be applied. Please try again.");
        button.disabled = false;
      }
    };
    button.addEventListener("click", apply);
    input.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      apply();
    });
    removeButton?.addEventListener("click", async () => {
      removeButton.disabled = true;
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {
        // The cart update remains authoritative when session storage is unavailable.
      }
      try {
        const updatedCart = await updateDiscount("");
        if (applicableCodes(updatedCart).length) throw new Error("Discount remains applied");
        window.location.reload();
      } catch {
        if (statusMessage) statusMessage.textContent = "The discount could not be removed. Please try again.";
        status?.classList.add("is-failed");
        removeButton.disabled = false;
      }
    });
    hydrateDiscountState();
  }

  function setupPurchaseChoices(root) {
    root.querySelectorAll("[data-anou-purchase-root]").forEach((purchaseRoot) => {
      const choices = purchaseRoot.querySelectorAll("[data-anou-purchase-choice]");
      const variantInput = purchaseRoot.querySelector("[data-anou-purchase-variant]");
      const price = purchaseRoot.querySelector("[data-anou-purchase-price]");
      const compare = purchaseRoot.querySelector("[data-anou-purchase-compare]");
      const cta = purchaseRoot.querySelector("[data-anou-purchase-cta]");
      const stickyVariant = root.querySelector("[data-anou-sticky-variant]");
      const stickyPrice = root.querySelector("[data-anou-sticky-price]");
      const stickyCta = root.querySelector("[data-anou-sticky-cta]");
      choices.forEach((choice) => {
        choice.addEventListener("click", () => {
          choices.forEach((item) => {
            const active = item === choice;
            item.classList.toggle("active", active);
            item.setAttribute("aria-checked", String(active));
          });
          if (variantInput) variantInput.value = choice.dataset.variantId || variantInput.value;
          if (price) price.textContent = choice.dataset.price || price.textContent;
          if (compare) {
            compare.textContent = choice.dataset.compareAt || "";
            compare.hidden = !choice.dataset.compareAt;
          }
          if (cta) {
            cta.classList.remove("is-aro", "is-dali", "is-duo");
            cta.classList.add(`is-${choice.dataset.slug || "duo"}`);
            cta.classList.toggle("soft-duo-buy-cta", choice.dataset.slug === "duo");
          }
          if (stickyVariant) stickyVariant.value = choice.dataset.variantId || stickyVariant.value;
          if (stickyPrice) stickyPrice.textContent = choice.dataset.price || stickyPrice.textContent;
          if (stickyCta) {
            stickyCta.classList.remove("is-aro", "is-dali", "is-duo", "is-carryall");
            stickyCta.classList.add(`is-${choice.dataset.slug || "duo"}`);
          }
        });
      });
    });
  }

  function setupStickyPdp(root) {
    const sticky = root.querySelector("[data-anou-sticky-pdp]");
    const purchase = root.querySelector("#soft-pdp-buy .soft-purchase-control");
    const dock = root.querySelector("[data-anou-support-dock]");
    if (!sticky || !purchase || !dock) return;
    // Keep the original product form and its single commerce owner inside the sheet.
    root.querySelector("[data-anou-support-sheet]")?.append(sticky);

    let ready;
    const setReady = (nextReady) => {
      if (ready === nextReady) return;
      ready = nextReady;
      sticky.setAttribute("data-visible", String(nextReady));
      sticky.setAttribute("aria-hidden", String(!nextReady));
      sticky.inert = !nextReady;
    };
    const update = () => {
      // The core owner publishes one atomic placement decision. Reading it also
      // handles either script startup order without a second scroll measurement.
      setReady(dock.dataset.pdpPurchaseVisible === "true");
    };
    dock.addEventListener("anou:support-placement", update);
    update();
  }

  function setupUsage(root) {
    root.querySelectorAll('[data-anou-usage-products]').forEach(group => {
      if (group.dataset.usageTabsReady) return;
      const tablist = group.querySelector('[data-anou-usage-tabs]');
      const tabs = [...group.querySelectorAll('[data-anou-usage-tab]')];
      const panels = [...group.querySelectorAll('[data-anou-usage-routine]')];
      if (!tablist || !tabs.length || tabs.length !== panels.length) return;
      group.dataset.usageTabsReady = 'true';
      const compact = matchMedia('(max-width: 1180px)');
      let selected = 0;
      const show = () => {
        tablist.hidden = !compact.matches;
        panels.forEach((panel, index) => {
          const active = index === selected;
          panel.hidden = compact.matches && !active;
          if (compact.matches) {
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('aria-labelledby', tabs[index].id);
            panel.tabIndex = active ? 0 : -1;
          } else {
            panel.removeAttribute('role');
            panel.removeAttribute('tabindex');
            panel.setAttribute('aria-labelledby', panel.querySelector('h3').id);
          }
          tabs[index].setAttribute('aria-selected', String(active));
          tabs[index].tabIndex = active ? 0 : -1;
        });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => { selected = index; show(); });
        tab.addEventListener('keydown', event => {
          const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length :
            event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length :
            event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : -1;
          if (next < 0) return;
          event.preventDefault();
          selected = next; show(); tabs[next].focus({ preventScroll:true });
        });
      });
      compact.addEventListener('change', () => {
        const focused = document.activeElement;
        show();
        if (compact.matches && panels.some(panel => panel.hidden && panel.contains(focused))) {
          tabs[selected].focus({ preventScroll:true });
        } else if (!compact.matches && tablist.contains(focused)) {
          panels[selected].setAttribute('tabindex', '-1');
          panels[selected].focus({ preventScroll:true });
        }
      });
      show();
    });
    root.querySelectorAll('[data-anou-usage-video]').forEach(row => {
      const video = row.querySelector('video');
      if (!video) return;
      const play = row.querySelector('[data-anou-usage-play]');
      if (play) {
        const syncPlay = () => { play.hidden = !video.paused; };
        play.addEventListener('click', () => {
          video.play().then(() => video.focus({ preventScroll:true })).catch(syncPlay);
        });
        ['play', 'pause', 'ended'].forEach(event => video.addEventListener(event, syncPlay));
        syncPlay();
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting) video.pause();
        }).observe(video);
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); });
    });
    const mobile = matchMedia("(max-width: 960px)");
    const visible = new Set();
    const schedules = new Map();
    const observer = "IntersectionObserver" in window ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          visible.add(target);
          schedules.get(target)?.();
        } else visible.delete(target);
      });
    }, { rootMargin: "240px 0px" }) : null;
    const canMeasure = (target) => mobile.matches && (!observer || visible.has(target));
    const watch = (target, schedule) => {
      schedules.set(target, schedule);
      if (observer) observer.observe(target);
      else schedule();
    };
    root.querySelectorAll(".soft-usage-guide").forEach((guide) => {
      const steps = Array.from(guide.querySelectorAll("[data-anou-usage-step]"));
      const panels = Array.from(guide.querySelectorAll("[data-anou-usage-panel]"));
      const flow = guide.querySelector(".soft-usage-flow");
      if (!steps.length) return;
      let activeStep = -1;
      const show = (active) => {
        if (active === activeStep) return;
        activeStep = active;
        steps.forEach((step, index) => {
          const item = step.closest("li");
          item?.classList.toggle("is-active", index === active);
          item?.classList.toggle("is-before", index < active);
          item?.classList.toggle("is-after", index > active);
          step.setAttribute("aria-pressed", String(index === active));
        });
        panels.forEach((panel, index) => {
          const visible = index === Math.min(active, panels.length - 1);
          panel.classList.toggle("is-active", visible);
          panel.setAttribute("aria-hidden", String(!visible));
        });
      };
      steps.forEach((step, index) => step.addEventListener("click", () => show(index)));
      let flowFrame = 0;
      const syncStepInView = () => {
        flowFrame = 0;
        if (!canMeasure(guide) || !flow) return;
        const flowRect = flow.getBoundingClientRect();
        const center = flowRect.left + flowRect.width / 2;
        const nearest = steps.reduce((best, step, index) => {
          const rect = step.closest("li")?.getBoundingClientRect() ?? step.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY });
        show(nearest.index);
      };
      const scheduleFlowSync = () => {
        if (canMeasure(guide) && !flowFrame) flowFrame = requestAnimationFrame(syncStepInView);
      };
      flow?.addEventListener("scroll", scheduleFlowSync, { passive: true });
      window.addEventListener("resize", scheduleFlowSync);
      mobile.addEventListener("change", scheduleFlowSync);
      show(0);
      watch(guide, scheduleFlowSync);
    });

    root.querySelectorAll(".soft-pdp-quadrant-board").forEach((board) => {
      const cards = Array.from(board.querySelectorAll(":scope > article"));
      if (!cards.length) return;
      let boardFrame = 0;
      let activeCard = -1;
      const setActive = (active) => {
        if (active === activeCard) return;
        activeCard = active;
        cards.forEach((card, index) => {
          const isActive = index === active;
          card.classList.toggle("is-active", isActive);
          if (isActive) card.setAttribute("aria-current", "true");
          else card.removeAttribute("aria-current");
        });
      };
      const syncCardInView = () => {
        boardFrame = 0;
        if (!canMeasure(board)) return;
        const boardRect = board.getBoundingClientRect();
        const center = boardRect.left + boardRect.width / 2;
        const nearest = cards.reduce((best, card, index) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY });
        setActive(nearest.index);
      };
      const scheduleCardSync = () => {
        if (canMeasure(board) && !boardFrame) boardFrame = requestAnimationFrame(syncCardInView);
      };
      board.addEventListener("scroll", scheduleCardSync, { passive: true });
      window.addEventListener("resize", scheduleCardSync);
      mobile.addEventListener("change", scheduleCardSync);
      watch(board, scheduleCardSync);
    });
  }

  function setupJudgeMeReviews(root) {
    const reviews = root.querySelector(".soft-judge-me-reviews");
    if (!reviews) return;
    // Presentation labels only. Customer text, data and schema Review types remain intact.
    const selectors = ".jdgm-rev-widg__title,.jdgm-rev-widg__summary-text,.jdgm-write-rev-link,.jdgm-sort-dropdown option,.jdgm-histogram__clear-filter,.jdgm-histogram__star,.jdgm-rev__transparency-badge,.jdgm-form__title,.jdgm-form label,.jdgm-form button,.jdgm-form input[placeholder],.jdgm-form input[type=submit],.jdgm-form textarea,.jdgm-cancel-rev,.jdgm-submit-rev,.jdgm-rev-widg__paginate-page,.jdgm-paginate__page";
    const rename = value => value.replace(/\breviews\b/gi, word => word[0] === "R" ? "Stories" : "stories").replace(/\breview\b/gi, word => word[0] === "R" ? "Story" : "story");
    let frame = 0;
    const adapt = () => {
      frame=0;
      reviews.querySelectorAll(selectors).forEach(element => {
        if (element.matches('.jdgm-histogram__clear-filter') && element.textContent.trim() && element.textContent !== 'Clear rating filter') {
          element.textContent = 'Clear rating filter';
        }
        for(const node of element.childNodes) if(node.nodeType === 3) { const next=rename(node.nodeValue);if(next!==node.nodeValue)node.nodeValue=next; }
        for(const attr of ["aria-label","title","placeholder",...(element.matches("input[type=submit]") ? ["value"] : [])]) {const old=element.getAttribute(attr);if(old&&rename(old)!==old)element.setAttribute(attr,rename(old));}
      });
    };
    adapt();
    new MutationObserver(()=>{if(!frame)frame=requestAnimationFrame(adapt);}).observe(reviews,{childList:true,subtree:true,characterData:true});
    // Use Judge.me's scroll hook instead of racing its jQuery animation. The
    // provider calls this before and after replacing the requested review page.
    let pagination = null;
    let paginationFrame = 0;
    let storyFrame = 0;
    let scrolling = false;
    let scrollTimer = 0;
    let scrollRequest = null;
    const visible = element => element.getClientRects().length > 0;
    const currentPage = widget => [...widget.querySelectorAll('.jdgm-paginate__page.jdgm-curt')].find(visible)?.getAttribute('data-page');
    const restoreHeight = request => {
      request?.restore?.();
      if (request) request.restore = null;
    };
    const finishPagination = request => {
      restoreHeight(request);
      request.restoreFocus?.();
      request.restoreFocus = null;
      clearTimeout(request.timeout);
      if (pagination === request) {
        cancelAnimationFrame(paginationFrame);
        pagination = null;
      }
    };
    const finishScroll = () => {
      scrolling = false;
      clearTimeout(scrollTimer);
      if (scrollRequest) finishPagination(scrollRequest);
      scrollRequest = null;
    };
    const scrollToStory = (element, focusTarget, request = null) => {
      const header = root.querySelector('.soft-mobile-topbar');
      const headerBottom = header?.getClientRects().length ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = Math.max(0, scrollY + element.getBoundingClientRect().top - headerBottom - 20);
      finishScroll();
      scrollRequest = request;
      scrolling = !reduced && Math.abs(top - scrollY) > 1;
      // One native animation owns the movement; focus must not start another one.
      window.scrollTo({ top, behavior: reduced ? 'instant' : 'smooth' });
      focusTarget?.focus({ preventScroll: true });
      if (scrolling) scrollTimer = setTimeout(finishScroll, 1500);
      else finishScroll();
    };
    window.addEventListener('scrollend', finishScroll);
    const settlePagination = request => {
      if (request.settling || request.scrolled) return;
      request.settling = true;
      let lastHeight = -1;
      let lastTop = -1;
      let stableSince = performance.now();
      const sample = now => {
        if (pagination !== request) return;
        if (request.cancelled) { finishPagination(request); return; }
        const body = [...request.widget.querySelectorAll('.jdgm-rev-widg__body')].find(visible);
        if (body && currentPage(request.widget) === request.page) {
          const rect = body.getBoundingClientRect();
          const top = rect.top + scrollY;
          // The current-page marker appears before Judge.me's slideDown finishes.
          if (Math.abs(rect.height - lastHeight) > 0.5 || Math.abs(top - lastTop) > 0.5) stableSince = now;
          lastHeight = rect.height;
          lastTop = top;
          if (rect.height > 0 && now - stableSince >= 100) {
            request.scrolled = true;
            if (request.keyboard) {
              body.setAttribute('tabindex', '-1');
              body.setAttribute('aria-label', `Customer stories, page ${request.page}`);
            }
            const focusTarget = request.focusTarget && body.contains(request.focusTarget) ? request.focusTarget : body;
            scrollToStory(body, request.keyboard ? focusTarget : null, request);
            return;
          }
        } else {
          stableSince = now;
          lastHeight = -1;
        }
        paginationFrame = requestAnimationFrame(sample);
      };
      paginationFrame = requestAnimationFrame(sample);
    };
    const settings = window.jdgmSettings = window.jdgmSettings || {};
    const previousScroll = settings.scrollTo;
    settings.scrollTo = function (target, offset) {
      const element = target?.[0];
      if (!pagination || !element || !pagination.widget.contains(element) || !element.matches('.jdgm-rev-widg__body')) {
        return previousScroll?.apply(this, arguments);
      }
      if (currentPage(pagination.widget) === pagination.page) settlePagination(pagination);
      return false;
    };
    const cancelPaginationScroll = () => {
      if (pagination) { pagination.cancelled = true; restoreHeight(pagination); }
      cancelAnimationFrame(storyFrame);
      if (scrolling) {
        finishScroll();
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }
    };
    window.addEventListener('wheel', cancelPaginationScroll, { passive: true });
    window.addEventListener('touchstart', cancelPaginationScroll, { passive: true });
    document.addEventListener('keydown', event => {
      if (['PageDown', 'PageUp', 'Home', 'End', 'ArrowDown', 'ArrowUp', ' '].includes(event.key)) cancelPaginationScroll();
    });
    const yieldToProvider = () => {
      cancelPaginationScroll();
      if (pagination) finishPagination(pagination);
    };
    reviews.addEventListener('change', event => {
      if (event.target.closest('.jdgm-sort-dropdown')) yieldToProvider();
    }, true);
    reviews.addEventListener('click', event => {
      const page = event.target.closest('.jdgm-paginate__page');
      if (!page) {
        if (event.target.closest('.jdgm-histogram__row,.jdgm-histogram__clear-filter,.jdgm-write-rev-link,.jdgm-cancel-rev')) yieldToProvider();
        return;
      }
      if (page.classList.contains('jdgm-curt')) return;
      const widget = page.closest('.jdgm-review-widget');
      if (!widget || !visible(page)) return;
      const height = widget.getBoundingClientRect().height;
      cancelPaginationScroll();
      if (pagination) finishPagination(pagination);
      const saved = ['min-height', 'overflow-anchor'].map(name => [name, widget.style.getPropertyValue(name), widget.style.getPropertyPriority(name)]);
      widget.style.setProperty('min-height', `${height}px`, 'important');
      widget.style.setProperty('overflow-anchor', 'none', 'important');
      const request = pagination = {
        widget, page: page.getAttribute('data-page'), keyboard: event.detail === 0, cancelled: false,
        restore: () => saved.forEach(([name, value, priority]) => value ? widget.style.setProperty(name, value, priority) : widget.style.removeProperty(name)),
      };
      // Judge.me also focuses the first result outside its scroll hook. Its plain
      // focus() starts another browser scroll while the new body is sliding open.
      const provider = window.jdgm;
      const previousFocus = provider?._focusFirstPaginatedItem;
      if (typeof previousFocus === 'function') {
        const focusFirst = function (target) {
          const container = target?.[0];
          if (pagination !== request || !container || !widget.contains(container)) return previousFocus.apply(this, arguments);
          if (request.cancelled) return;
          const first = target.find('.jdgm-rev, .jdgm-quest').first();
          if (!first.length) return;
          first.attr('tabindex', '-1');
          request.focusTarget = first[0];
          return first[0].focus({ preventScroll: true });
        };
        provider._focusFirstPaginatedItem = focusFirst;
        request.restoreFocus = () => {
          if (provider._focusFirstPaginatedItem === focusFirst) provider._focusFirstPaginatedItem = previousFocus;
        };
      }
      request.timeout = setTimeout(() => finishPagination(request), 15000);
    }, true);
    root.addEventListener('click', event => {
      const link = event.target.closest('a[href="#customer-reviews"]');
      if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      cancelPaginationScroll();
      const accordion = reviews.querySelector('.soft-pdp-review-accordion');
      if (accordion) accordion.open = true;
      if (location.hash !== '#customer-reviews') history.pushState(history.state, '', '#customer-reviews');
      storyFrame = requestAnimationFrame(() => {
        scrollToStory(reviews, event.detail === 0 ? accordion?.querySelector('summary') : null);
      });
    }, true);
    let pendingOpenTimers = [];

    reviews.addEventListener("click", (event) => {
      const trigger = event.target.closest(".jdgm-write-rev-link");
      if (!trigger) return;

      pendingOpenTimers.forEach(clearTimeout);
      pendingOpenTimers = [];
      if (trigger.getAttribute("aria-expanded") === "true") return;

      const revealForm = () => {
        const currentForm = reviews.querySelector(".jdgm-form-wrapper");
        if (!currentForm || getComputedStyle(currentForm).display !== "none") return;
        currentForm.style.setProperty("display", "block", "important");
        currentForm.removeAttribute("aria-hidden");
        const currentTrigger = reviews.querySelector(".jdgm-write-rev-link");
        currentTrigger?.setAttribute("aria-expanded", "true");
        currentTrigger?.classList.add("close-form-btn");
      };
      pendingOpenTimers = [0, 180, 480].map((delay) => setTimeout(revealForm, delay));
      // Judge.me scrolls its form without accounting for our fixed mobile header.
      pendingOpenTimers.push(setTimeout(() => {
        if (!matchMedia("(max-width: 960px)").matches) return;
        const heading = reviews.querySelector(".jdgm-form__title");
        const header = root.querySelector(".soft-mobile-topbar");
        if (!heading?.getClientRects().length || !header) return;
        const top = heading.getBoundingClientRect().top;
        const clearTop = header.getBoundingClientRect().bottom + 12;
        // Leave later manual scrolling alone; only correct the opening position.
        if (top > -64 && top < clearTop) window.scrollBy({ top: top - clearTop, behavior: "instant" });
      }, 650));
    }, true);
  }

  function setupParityVideos(root) {
    root.querySelectorAll("[data-anou-parity-video]").forEach((frame) => {
      const video = frame.querySelector("video");
      if (!video) return;
      let activated = false;
      const activate = () => {
        if (activated) return;
        video.querySelectorAll("source[data-src]").forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute("data-src");
        });
        activated = true;
        video.load();
      };
      video.addEventListener("playing", () => frame.classList.add("is-video-playing"));
      video.addEventListener("pause", () => frame.classList.remove("is-video-playing"));
      video.addEventListener("error", () => frame.classList.remove("is-video-playing"));
      if (!("IntersectionObserver" in window)) {
        activate();
        video.play().catch(() => frame.classList.remove("is-video-playing"));
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            activate();
            video.play().catch(() => frame.classList.remove("is-video-playing"));
          } else {
            video.pause();
          }
        },
        { rootMargin: "160px" },
      );
      observer.observe(frame);
    });
  }

  function setupScrollSignal(root) {
    const signalRoot = root.querySelector(
      ".soft-home, .soft-product-page, .soft-story-page, .soft-info-page, .soft-formula-page, .checkout-page",
    ) || root;
    const panel = signalRoot.querySelector(".soft-left-panel");
    const menu = panel?.querySelector(".soft-left-menu");
    const signal = panel?.querySelector(".soft-scroll-signal");
    const sigil = signal?.querySelector(".soft-scroll-sigil");
    if (!panel || !menu || !signal || !sigil) return;
    const desktop = matchMedia("(min-width: 961px)");
    let frame = 0;
    let previousTop;
    let previousBottom;
    let previousMarker;
    let previousMenuHeight;

    const update = () => {
      frame = 0;
      if (!desktop.matches) return;
      // Measure once before writing; the signal has no border or padding.
      const panelRect = panel.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const margin = Math.round(Math.min(Math.max(innerHeight * 0.018, 12), 20));
      const bottomAnchors = Array.from(
        signalRoot.querySelectorAll(".soft-rail-quiz, .soft-support-card, .soft-product-summary"),
      ).map((node) => node.getBoundingClientRect()).filter((rect) => rect.height > 0);
      const topAnchor = bottomAnchors.sort(
        (a, b) => a.top - b.top,
      )[0];
      const top = Math.round(Math.max(0, menuRect.bottom - panelRect.top + margin));
      const bottom = Math.round(topAnchor
        ? Math.max(margin, panelRect.bottom - topAnchor.top + margin)
        : margin);
      const max = Math.max(1, document.documentElement.scrollHeight - innerHeight);
      const progress = Math.min(1, Math.max(0, scrollY / max));
      const travel = Math.max(0, panel.clientHeight - top - bottom - sigil.clientHeight);
      const marker = Math.round(progress * travel);
      const menuHeight = Math.max(0, Math.floor(innerHeight - menuRect.bottom - margin - 14));
      // Keep inherited variables on their consumers, not the entire page/rail.
      if (menuHeight !== previousMenuHeight) menu.style.setProperty("--soft-menu-available-height", `${menuHeight}px`);
      if (top !== previousTop) signal.style.setProperty("--soft-scroll-signal-top", `${top}px`);
      if (bottom !== previousBottom) signal.style.setProperty("--soft-scroll-signal-bottom", `${bottom}px`);
      if (marker !== previousMarker) sigil.style.setProperty("--soft-scroll-marker-y", `${marker}px`);
      previousTop = top;
      previousBottom = bottom;
      previousMarker = marker;
      previousMenuHeight = menuHeight;
    };
    const schedule = () => { if (desktop.matches && !frame) frame = requestAnimationFrame(update); };
    addEventListener("scroll", schedule, { passive: true });
    addEventListener("resize", schedule);
    desktop.addEventListener("change", schedule);

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(schedule);
    if (resizeObserver) {
      resizeObserver.observe(panel);
      panel.querySelectorAll(".soft-left-menu, .soft-rail-quiz, .soft-support-card")
        .forEach((node) => resizeObserver.observe(node));
    }
    update();
  }

  function setupReadSectionNavigation(root) {
    root.querySelectorAll(".soft-read-page").forEach((page) => {
      const guide = page.querySelector("[data-anou-section-guide]");
      if (!guide) return;
      const links = Array.from(guide.querySelectorAll("[data-section-id]"));
      const sectionIds = links.map((link) => link.getAttribute("data-section-id")).filter(Boolean);
      if (!sectionIds.length) return;
      const sections = sectionIds.map((id) => document.getElementById(id));

      let frame = 0;
      let currentSectionId = null;
      let recenterOnUpdate = false;
      let observer;
      const activate = (sectionId, recenter = false) => {
        if (sectionId === currentSectionId && !recenter) return;
        currentSectionId = sectionId;
        const activeLink = links.find((link) => link.getAttribute("data-section-id") === sectionId);
        // Read the guide before changing active styles, once per section change.
        const left = activeLink && guide.scrollWidth > guide.clientWidth
          ? Math.max(0, activeLink.offsetLeft - (guide.clientWidth - activeLink.offsetWidth) / 2)
          : null;
        links.forEach((link) => {
          const active = link.getAttribute("data-section-id") === sectionId;
          link.classList.toggle("is-active", active);
          if (active) link.setAttribute("aria-current", "location");
          else link.removeAttribute("aria-current");
        });

        if (left === null) return;
        const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
        guide.scrollTo({ left, behavior: reducedMotion ? "auto" : "smooth" });
      };
      const update = () => {
        frame = 0;
        const activationLine = innerWidth <= 960 ? 142 : 112;
        let activeSectionId = sectionIds[0];
        sections.some((section, index) => {
          if (!section) return false;
          if (section.getBoundingClientRect().top > activationLine) return true;
          activeSectionId = sectionIds[index];
          return false;
        });
        activate(activeSectionId, recenterOnUpdate);
        recenterOnUpdate = false;
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      const syncHash = () => {
        let hashSectionId;
        try { hashSectionId = decodeURIComponent(location.hash.slice(1)); }
        catch { return; }
        if (sectionIds.includes(hashSectionId)) activate(hashSectionId, true);
      };
      const observeSections = () => {
        observer?.disconnect();
        const line = Math.min(innerHeight, innerWidth <= 960 ? 142 : 112);
        // Watch crossings of the top reading band instead of reading every
        // section's layout on every scroll frame. Scroll end covers large jumps
        // that skip the band, including scrollbar dragging and restored history.
        observer = new IntersectionObserver(schedule, {
          rootMargin: `0px 0px ${line - innerHeight}px 0px`, threshold: 0,
        });
        sections.filter(Boolean).forEach((section) => observer.observe(section));
      };

      links.forEach((link) => {
        link.addEventListener("click", (event) => {
          if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
          activate(link.getAttribute("data-section-id"), true);
          // The real href owns scrolling, focus, history and opening a new tab.
        });
      });
      if (typeof IntersectionObserver === "function") observeSections();
      else addEventListener("scroll", schedule, { passive: true });
      addEventListener("scrollend", schedule, { passive: true });
      addEventListener("resize", () => {
        if (observer) observeSections();
        recenterOnUpdate = true;
        schedule();
      });
      addEventListener("hashchange", syncHash);
      syncHash();
      if (!observer) schedule();
      page.setAttribute("data-section-nav-ready", "true");
    });
  }
})();
